import React, { useMemo, useState } from "react";
import { fmt } from "@/lib/format";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";
import { Newspaper, ExternalLink } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "全部" },
  { key: "營運", label: "營運" },
  { key: "財報", label: "財報" },
  { key: "產業", label: "產業" },
  { key: "法規", label: "法規/訴訟" },
  { key: "市場情緒", label: "市場情緒" },
];

function SentimentBadge({ s }) {
  const map = {
    positive: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
    negative: "bg-red-500/10 border-red-500/20 text-red-400",
    neutral: "bg-zinc-500/10 border-zinc-500/20 text-zinc-400",
  };
  const label = { positive: "正面", negative: "負面", neutral: "中性" };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-medium text-[10px] uppercase tracking-widest ${map[s]}`}
    >
      {label[s]}
    </span>
  );
}

export default function NewsFeed({ news = [] }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => (filter === "all" ? news : news.filter((n) => n.category === filter)),
    [filter, news]
  );

  return (
    <section>
      <SectionTitle icon={Newspaper} title="最新新聞摘要" subtitle={`News · ${news.length} 則`} />

      <div className="flex flex-wrap gap-1.5 mb-3">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
              filter === c.key
                ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="card-tactical divide-y divide-zinc-800" data-testid={TID.newsFeed}>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500">此分類目前無新聞。</div>
        )}
        {filtered.map((n, i) => (
          <article key={i} className="p-4 sm:p-5 hover:bg-zinc-900/40 transition-colors" data-testid={TID.newsItem}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                  {n.source}
                </span>
                <span className="text-zinc-700">·</span>
                <span className="text-[10px] font-mono text-zinc-500">{fmt.date(n.date)}</span>
                <SentimentBadge s={n.sentiment} />
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-900 border border-zinc-800 text-zinc-400">
                  {n.category}
                </span>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-zinc-600 shrink-0" strokeWidth={1.5} />
            </div>
            <h4 className="text-sm sm:text-base font-semibold text-white leading-snug">
              {n.title}
            </h4>
            <p className="mt-1.5 text-xs sm:text-sm text-zinc-400 leading-relaxed">{n.summary}</p>
            <div className="mt-2 text-xs text-zinc-500">
              <span className="text-blue-300 font-semibold">影響：</span>
              {n.impact}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
